<script setup>
import AbstractGroup from "./../AbstractGroup.vue";
import Icon from "../../../Common/Icon.vue";
import { computed, watch } from "vue";
import { ref } from "vue";
import HierarchyRow from "./HierarchyRow.vue";

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
});

// Nesting
const nestedOptions = computed(() => {
  const optionMap = {};
  const rootOptions = [];

  Object.values(props.modelValue).forEach((option) => {
    optionMap[option.id] = { ...option, children: [] };
  });

  Object.values(optionMap).forEach((option) => {
    if (option.parent !== null && optionMap[option.parent]) {
      optionMap[option.parent].children.push(option);
    } else {
      rootOptions.push(option);
    }
  });

  return rootOptions;
});

// Active Options
const activeOptions = computed(() => {
  return Object.values(props.modelValue).filter((option) => option.active);
});

const hasActiveOptions = computed(() => {
  return activeOptions.value.length > 0;
});

// Search
const searchTerm = ref("");

const isSearch = computed(() => {
  return searchTerm.value.length > 0;
});

const searchOptions = computed(() => {
  const lowerSearchTerm = searchTerm.value.toLowerCase();
  return Object.values(props.modelValue).filter((option) => option.name.toLowerCase().includes(lowerSearchTerm));
});

// Breadcrumbs
function getBreadcrumbs(id) {
  if (props.modelValue[id].parent === null) {
    return null;
  }

  const names = [];
  let currentId = props.modelValue[id].parent;

  while (currentId !== null) {
    names.push(props.modelValue[currentId].name);
    currentId = props.modelValue[currentId].parent;
  }

  return names.reverse().join(" > ") + " >";
}

// Check/uncheck children
function onFilterChange(id) {
  const children = getOptionChildren(id);
  const parentValue = props.modelValue[id].active;

  if (children.length > 0) {
    children.forEach((child) => (props.modelValue[child.id].active = parentValue));
  }
}

function getOptionChildren(id) {
  const allChildren = [];

  function findAllChildren(options) {
    for (const option of options) {
      if (option.id === id) {
        collectChildren(option);
        return;
      }
      findAllChildren(option.children);
    }
  }

  const collectChildren = (option) => {
    allChildren.push(option);
    for (const child of option.children) {
      collectChildren(child);
    }
  };

  findAllChildren(nestedOptions.value);
  return allChildren.slice(1);
}
</script>

<template>
  <AbstractGroup class="group">
    <template #header>
      <div :class="hasActiveOptions ? 'text-primary' : ''" class="transition group-hover:translate-x-2">
        <span v-t="`filter.group.${props.name}`"> </span>
        <span v-if="hasActiveOptions" v-text="` (${activeOptions.length})`"> </span>
      </div>
    </template>
    <template #inner>
      <div class="text-16 lg:text-20">
        <div class="px-10 lg:px-16 py-6 flex items-center gap-x-4 border-b border-dark/10">
          <Icon name="search" class="shrink-0 size-4 text-dark/80" />
          <label :for="`${props.name}-search`" class="sr-only" v-t="`filter.group.${props.name}.search.label`"> </label>
          <input
            type="text"
            v-model="searchTerm"
            :id="`${props.name}-search`"
            :name="`${props.name}-search`"
            :placeholder="$t(`filter.group.${props.name}.search.placeholder`)"
            class="bg-transparent outline-none font-medium placeholder:text-dark/50 w-full grow"
          />
        </div>
        <div class="px-10 lg:px-16 py-6 lg:py-10">
          <div v-if="!isSearch" class="space-y-4">
            <HierarchyRow
              :name="props.name"
              v-for="option in nestedOptions"
              :key="option.id"
              :id="option.id"
              :children="option.children"
              v-model="props.modelValue"
              @filterChange="onFilterChange($event)"
            />
          </div>

          <div v-else-if="isSearch" class="space-y-4">
            <div
              v-if="searchOptions.length > 0"
              v-for="option in searchOptions"
              :key="option.id"
              class="flex items-center gap-x-4"
            >
              <input
                type="checkbox"
                :name="`${props.name}-option-${option.id}`"
                :id="`${props.name}-option-${option.id}`"
                class="accent-primary size-4"
                :value="option.id"
                v-model="props.modelValue[option.id].active"
                @change="onFilterChange(option.id)"
              />
              <label :for="`${props.name}-option-${option.id}`">
                <span v-text="getBreadcrumbs(option.id)" class="block text-12 text-dark/50 leading-none -mb-1"> </span>
                <span v-text="option.name"></span>
              </label>
            </div>
            <p v-else v-t="`filter.group.${props.name}.options.no-results`" class="text-12 lg:text-16 text-dark/50"></p>
          </div>
        </div>
      </div>
    </template>
  </AbstractGroup>
</template>
